import { useRef, useState } from "react";
import { Camera, X, ShieldCheck, Images } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { fileToResizedDataUrl } from "@/lib/image";
import api from "@/lib/api";
import { toast } from "sonner";

export function ImageSlot({ label, value, onChange, watermark = false, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      let dataUrl = await fileToResizedDataUrl(file);
      if (watermark) {
        const res = await api.post("/utils/watermark", { image: dataUrl });
        dataUrl = res.data.image;
        toast.success("NRIC stamped with security watermark");
      }
      onChange(dataUrl);
    } catch {
      toast.error("Failed to process image");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        className={`relative flex aspect-[4/3] cursor-pointer items-center justify-center overflow-hidden rounded-sm border-2 border-dashed transition-colors ${
          value ? "border-primary/60" : "border-input hover:border-primary/50 hover:bg-primary/5"
        }`}
      >
        {value ? (
          <>
            <img src={value} alt={label} className="h-full w-full object-cover" />
            <button
              type="button"
              data-testid={`${testid}-remove`}
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              className="absolute right-2 top-2 rounded-full bg-black/70 p-1.5 text-white transition-colors hover:bg-destructive"
            >
              <X size={14} />
            </button>
            {watermark && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-sm bg-black/70 px-2 py-1">
                <ShieldCheck size={12} className="text-primary" />
                <span className="text-[9px] font-bold uppercase tracking-wider text-primary">Watermarked</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <Camera size={28} className="text-muted-foreground" />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{busy ? "Processing..." : label}</span>
            {watermark && <span className="text-[9px] uppercase tracking-wider text-primary">Auto watermark on upload</span>}
          </div>
        )}
      </div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid={`${testid}-input`} />
    </div>
  );
}

export function AccidentBatchUpload({ values, onChange, max = 10, testid = "accident-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = max - values.length;
    if (room <= 0) {
      toast.error(`Maximum ${max} photos allowed`);
      return;
    }
    setBusy(true);
    try {
      const processed = [];
      for (const f of files.slice(0, room)) {
        processed.push(await fileToResizedDataUrl(f, 1024, 0.75));
      }
      onChange([...values, ...processed]);
    } catch {
      toast.error("Failed to process images");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div
        data-testid={`${testid}-zone`}
        onClick={() => inputRef.current?.click()}
        className="flex cursor-pointer items-center justify-center gap-3 rounded-sm border-2 border-dashed border-input px-6 py-8 transition-colors hover:border-primary/50 hover:bg-primary/5"
      >
        <Images size={24} className="text-muted-foreground" />
        <div>
          <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground">
            {busy ? "Processing..." : "Add accident scene photos"}
          </p>
          <p className="text-xs text-muted-foreground">Min 1 · Max {max} photos ({values.length}/{max} added)</p>
        </div>
      </div>
      {values.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {values.map((v, i) => (
            <div key={v} className="relative aspect-square overflow-hidden rounded-sm border border-border">
              <img src={v} alt={`accident-${i}`} className="h-full w-full object-cover" />
              <button
                type="button"
                data-testid={`accident-remove-${i}`}
                onClick={() => onChange(values.filter((_, idx) => idx !== i))}
                className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white transition-colors hover:bg-destructive"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFiles} data-testid={`${testid}-input`} />
    </div>
  );
}

export function RoadtaxUpload({ value, unable, onPhoto, onUnable }) {
  return (
    <div className="space-y-3">
      {!unable && (
        <ImageSlot label="Roadtax Photo" value={value} onChange={onPhoto} testid="roadtax-upload" />
      )}
      <label className="flex cursor-pointer items-center gap-3 rounded-sm border border-input bg-card px-4 py-3 transition-colors hover:border-primary/40">
        <Checkbox
          data-testid="roadtax-unable-checkbox"
          checked={unable}
          onCheckedChange={(v) => onUnable(!!v)}
        />
        <span className="text-sm font-semibold">Unable to take picture</span>
      </label>
    </div>
  );
}
